import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { DateTime } from "luxon";
import type { RecurringRateType } from "../recurring-rate";
import { allocatePerPeriodAmounts, summarizeDeductionAmounts } from "./allocate-per-period-amounts";
import { selectDeductedPeriods, selectPeriodsInCollectionWindow } from "./select-pay-periods";

export { anchorCollectionBoundary, anchorCollectionWindow } from "./anchor-collection-window";
export { resolvePurchaseStart } from "./resolve-purchase-start";
export { allocatePerPeriodAmounts, summarizeDeductionAmounts } from "./allocate-per-period-amounts";
export type { DeductionAmounts } from "./allocate-per-period-amounts";
export { isPeriodDeducted, selectDeductedPeriods, selectPeriodsInCollectionWindow } from "./select-pay-periods";

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";

export const payFrequencyMessages: Record<PayFrequency, MessageDescriptor> = {
	weekly: msg`Weekly`,
	biweekly: msg`Bi-Weekly`,
	semimonthly: msg`Semi-Monthly`,
	monthly: msg`Monthly`,
};

export type PaymentScheduleSummary = {
	perPeriodAmountInCents: number;
	deductedTotalInCents: number;
	remainderDeductions?: { amountInCents: number; count: number };
	deductionCount: number;
	firstDeductionDate: string | null;
	lastDeductionDate: string | null;
	payFrequency?: PayFrequency;
	/** The rate runs until the permit is cancelled, so the deduction count is only what the schedule defines today. */
	isOngoing?: boolean;
};

const median = (values: number[]): number => {
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
};

const isDayAnchoredSemiMonthly = (sortedPayDates: string[]): boolean => {
	const anchorDays = new Set(sortedPayDates.map((payDate) => payDate.slice(8, 10)));
	return sortedPayDates.length >= 3 && anchorDays.size === 2;
};

export const inferPayFrequency = (payDates: string[]): PayFrequency | undefined => {
	if (payDates.length < 2) {
		return undefined;
	}
	const sorted = [...payDates].sort((a, b) => a.localeCompare(b));
	const gaps = sorted
		.slice(1)
		.map(
			(payDate, index) =>
				DateTime.fromISO(payDate, { zone: "utc" }).diff(
					DateTime.fromISO(sorted[index]!, { zone: "utc" }),
					"days",
				).days,
		);
	const medianGap = median(gaps);
	if (medianGap >= 6 && medianGap <= 8) {
		return "weekly";
	}
	if (medianGap >= 13 && medianGap <= 14) {
		return isDayAnchoredSemiMonthly(sorted) ? "semimonthly" : "biweekly";
	}
	if (medianGap >= 15 && medianGap <= 16) {
		return "semimonthly";
	}
	if (medianGap >= 28 && medianGap <= 31) {
		return "monthly";
	}
	return undefined;
};

type PayPeriod = {
	startDate: Date;
	endDate: Date;
	payDate?: Date | null;
};

const toDeductionDate = (payDate?: Date | null) =>
	payDate ? DateTime.fromJSDate(payDate, { zone: "utc" }).toISODate() : null;

export const getPaymentScheduleSummary = ({
	payPeriods,
	totalAmountInCents,
	collectionStartDate,
	collectionEndDate,
	permitStart,
	permitEnd,
	rateType = "FIXED_TOTAL",
}: {
	payPeriods: PayPeriod[];
	/** A total to split across the window for `FIXED_TOTAL`, the amount charged every period for `RECURRING`. */
	totalAmountInCents: number;
	collectionStartDate?: Date | null;
	collectionEndDate?: Date | null;
	permitStart?: Date | null;
	permitEnd?: Date | null;
	rateType?: RecurringRateType;
}): PaymentScheduleSummary | undefined => {
	const periodsInWindow = selectPeriodsInCollectionWindow({ payPeriods, collectionStartDate, collectionEndDate });
	const deductedPeriods = permitStart
		? selectDeductedPeriods({ payPeriods: periodsInWindow, permitStart, permitEnd })
		: periodsInWindow;

	const firstPeriod = deductedPeriods[0];
	const lastPeriod = deductedPeriods[deductedPeriods.length - 1];
	if (!firstPeriod || !lastPeriod) {
		return undefined;
	}
	const firstDeductedIndex = periodsInWindow.indexOf(firstPeriod);

	const payFrequency = inferPayFrequency(
		deductedPeriods
			.map((period) => toDeductionDate(period.payDate))
			.filter((payDate): payDate is string => payDate !== null),
	);

	const deductionAmounts =
		rateType === "RECURRING"
			? {
					perPeriodAmountInCents: totalAmountInCents,
					deductedTotalInCents: totalAmountInCents * deductedPeriods.length,
				}
			: summarizeDeductionAmounts({
					amountsInCents: allocatePerPeriodAmounts({
						totalInCents: totalAmountInCents,
						periodCount: periodsInWindow.length,
					}).slice(firstDeductedIndex, firstDeductedIndex + deductedPeriods.length),
				});

	const isOngoing = rateType === "RECURRING" && !collectionEndDate && !permitEnd;

	return {
		...deductionAmounts,
		deductionCount: deductedPeriods.length,
		firstDeductionDate: toDeductionDate(firstPeriod.payDate),
		lastDeductionDate: toDeductionDate(lastPeriod.payDate),
		...(payFrequency ? { payFrequency } : {}),
		...(isOngoing ? { isOngoing } : {}),
	};
};
